import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useConversation } from "@elevenlabs/react";
import { supabase } from "@/integrations/supabase/client";
import VoiceOrb from "@/components/VoiceOrb";
import { X, Mic, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { AGENT_ID } from "@/config/agent-tools";
import { resolveImmediateVoiceAction } from "@/lib/voiceImmediateActions";
import { useBridgetteActionExecutor } from "@/hooks/useBridgetteActionExecutor";
import { BridgetteApprovalDialog } from "@/components/bridgette/BridgetteApprovalDialog";
import { useTrade } from "@/contexts/TradeContext";

/**
 * Bridgette voice co-pilot — clean architecture.
 *
 *   ElevenLabs  = STT (user_transcript) + TTS only. Zero hosted tools.
 *   bridgette-brain (Together.ai) = sole decision maker. Returns { spoken_response, action }.
 *   This widget = transcript -> fast-path or brain -> execute action -> sendContextualUpdate to TTS.
 *
 * No client tools. No compatibility shims. No Lovable AI in the voice path.
 */
export function GlobalRoofingCoPilot() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [instantQuoteOpen, setInstantQuoteOpen] = useState(false);
  const [transcript, setTranscript] = useState<Array<{ role: "user" | "agent"; text: string }>>([]);
  const [voiceStatus, setVoiceStatus] = useState<"idle" | "connecting" | "connected" | "speaking">("idle");
  const [lang, setLang] = useState<"en" | "ru">(() => {
    if (typeof window === "undefined") return "en";
    return (window.localStorage.getItem("bridgette_lang") as "en" | "ru") || "en";
  });
  const permissionStreamRef = useRef<MediaStream | null>(null);
  const sessionStartLockRef = useRef(false);
  const inFlightTranscriptRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { user } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  // Always show the FAB on every route, including the workspace.
  const isWorkspaceRoute = false;

  const { execute, pendingApproval, approve, cancel } = useBridgetteActionExecutor();
  const { activeTradeCode } = useTrade();

  const hiddenOnCurrentRoute = location.pathname === "/jobs/new/ai";

  const firstName = useMemo(() => {
    const rawName =
      (user as any)?.user_metadata?.first_name ||
      (user as any)?.user_metadata?.full_name?.split?.(" ")?.[0] ||
      user?.email?.split?.("@")[0] ||
      "";
    return rawName ? rawName.charAt(0).toUpperCase() + rawName.slice(1) : "";
  }, [user]);

  const appendTranscript = useCallback((role: "user" | "agent", text: string) => {
    const trimmed = (text || "").trim();
    if (!trimmed) return;
    setTranscript((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === role && last?.text === trimmed) return prev;
      return [...prev, { role, text: trimmed }];
    });
  }, []);

  // -------- Connection lifecycle --------
  const handleConnect = useCallback(() => {
    sessionStartLockRef.current = false;
    if (permissionStreamRef.current) {
      permissionStreamRef.current.getTracks().forEach((t) => t.stop());
      permissionStreamRef.current = null;
    }
    setVoiceStatus("connected");
  }, []);

  const handleDisconnect = useCallback(() => {
    sessionStartLockRef.current = false;
    if (permissionStreamRef.current) {
      permissionStreamRef.current.getTracks().forEach((t) => t.stop());
      permissionStreamRef.current = null;
    }
    setVoiceStatus("idle");
  }, []);

  const handleError = useCallback((error: any) => {
    console.error("❌ Voice error:", error);
    sessionStartLockRef.current = false;
    if (permissionStreamRef.current) {
      permissionStreamRef.current.getTracks().forEach((t) => t.stop());
      permissionStreamRef.current = null;
    }
    setVoiceStatus("idle");
    toast.error(error?.message || "Voice error");
  }, []);

  // -------- Brain routing (declared via ref so the memoized message handler can call it) --------
  const brainRouterRef = useRef<((utterance: string) => Promise<void>) | null>(null);

  // Speak a string back with deterministic TTS so the spoken output matches
  // the exact language/text returned by Bridgette's brain.
  const speak = useCallback(
    async (text: string) => {
      const trimmed = (text || "").trim();
      if (!trimmed) return;
      appendTranscript("agent", trimmed);

      try {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }

        setVoiceStatus("speaking");

        const { data, error } = await supabase.functions.invoke("tts-service", {
          body: {
            provider: "openai",
            text: trimmed,
          },
        });

        if (error) throw error;
        const audioContent = data?.audioContent;
        if (!audioContent) throw new Error("No audio content returned");

        const audio = new Audio(`data:audio/mpeg;base64,${audioContent}`);
        audioRef.current = audio;
        audio.onended = () => {
          audioRef.current = null;
          setVoiceStatus("connected");
        };
        audio.onerror = () => {
          audioRef.current = null;
          setVoiceStatus("connected");
          throw new Error("Audio playback failed");
        };

        await audio.play();
      } catch (e) {
        console.warn("TTS playback failed, falling back to browser speech:", e);
        try {
          window.speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(trimmed);
          utterance.lang = lang === "ru" ? "ru-RU" : "en-US";
          utterance.onend = () => setVoiceStatus("connected");
          utterance.onerror = () => setVoiceStatus("connected");
          window.speechSynthesis.speak(utterance);
        } catch (fallbackError) {
          console.warn("Browser speech fallback failed:", fallbackError);
          setVoiceStatus("connected");
        }
      }
    },
    [appendTranscript, lang],
  );

  // -------- Message parsing (ElevenLabs events, voice only) --------
  const handleMessage = useCallback(
    (message: any) => {
      if (!message || typeof message !== "object") return;
      const eventType = message.type;

      // Ignore noisy / unused events
      if (
        eventType === "audio" ||
        eventType === "vad_score" ||
        eventType === "agent_tool_response" ||
        eventType === "conversation_initiation_metadata" ||
        eventType === "conversation_initiation_client_data"
      ) return;

      if (eventType === "error") {
        const msg = message.error?.message || message.message || "Voice session error";
        console.error("❌ ElevenLabs error event:", msg);
        toast.error(msg);
        return;
      }

      if (eventType === "user_transcript") {
        const txt: string =
          message.user_transcription_event?.user_transcript ??
          message.user_transcript ??
          "";
        const trimmed = txt.trim();
        if (!trimmed) return;

        appendTranscript("user", trimmed);

        if (inFlightTranscriptRef.current === trimmed) return;
        inFlightTranscriptRef.current = trimmed;
        void brainRouterRef.current?.(trimmed).finally(() => {
          if (inFlightTranscriptRef.current === trimmed) inFlightTranscriptRef.current = null;
        });
        return;
      }

      if (eventType === "agent_response") {
        const txt = message.agent_response_event?.agent_response ?? message.agent_response ?? "";
        appendTranscript("agent", txt);
        return;
      }

      if (eventType === "agent_response_correction") {
        const txt = message.agent_response_correction_event?.corrected_agent_response ?? "";
        appendTranscript("agent", txt);
        return;
      }

      // Hosted-agent tool calls should never happen now — log & ignore if they do.
      if (eventType === "client_tool_call") {
        console.warn("Ignoring stray hosted-agent tool call:", message.client_tool_call);
        return;
      }
    },
    [appendTranscript],
  );

  const routeUtterance = useCallback(
    async (utterance: string) => {
      try {
        if (window.location.pathname.startsWith("/bridgette/workspace")) {
          window.dispatchEvent(new CustomEvent("bridgette-workspace-voice-command", { detail: { utterance } }));
          await speak(
            lang === "ru"
              ? "Я отправила это в workspace, чтобы использовать загруженные документы и очередь подтверждения."
              : "I sent that into the workspace so I can use the uploaded documents and approval queue.",
          );
          return;
        }

        // Fast-path: deterministic navigation/tab/form intents resolved locally.
        const fast = resolveImmediateVoiceAction(utterance, window.location.pathname, lang);
        if (fast) {
          const { spoken } = await execute(
            { type: fast.type, parameters: fast.parameters },
            fast.spokenResponse,
            lang,
          );
          await speak(spoken || fast.spokenResponse);
          return;
        }

        // Otherwise: ask the brain.
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        const userId = sessionData?.session?.user?.id;
        if (!token || !userId) throw new Error("Session expired");

        const { data: profile } = await supabase
          .from("profiles")
          .select("org_id")
          .eq("id", userId)
          .single();

        const path = window.location.pathname;
        const jobIdMatch = path.match(/\/jobs\/([a-f0-9-]+)/);
        const leadIdMatch = path.match(/\/leads\/([a-f0-9-]+)/);

        const { data, error } = await supabase.functions.invoke("bridgette-brain", {
          body: {
            user_input: utterance,
            context: {
              path,
              job_id: jobIdMatch?.[1] ?? null,
              lead_id: leadIdMatch?.[1] ?? null,
              org_id: profile?.org_id ?? null,
              user_id: userId,
              trade_code: activeTradeCode,
              language: lang,
            },
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (error) throw error;

        const spokenFromBrain: string = data?.spoken_response || "Done.";
        const action = data?.action || null;

        const { spoken } = await execute(action, spokenFromBrain, lang);
        await speak(spoken || spokenFromBrain);
      } catch (e: any) {
        console.error("❌ Brain routing failed:", e);
        await speak(
          lang === "ru"
            ? `У меня возникла ошибка: ${e?.message || "неизвестно"}`
            : `I hit an error: ${e?.message || "unknown"}`,
        );
      }
    },
    [execute, lang, speak],
  );

  const conversation = useConversation(
    useMemo(
      () => ({
        // ZERO client tools. ElevenLabs is voice-only.
        // NO overrides — only "Agent language" + "Text only" are whitelisted in the
        // dashboard. Sending prompt/firstMessage/language overrides triggers a 1008
        // policy violation that kills the session before she ever speaks.
        // Russian mode is handled post-connect via sendContextualUpdate.
        onConnect: handleConnect,
        onDisconnect: handleDisconnect,
        onError: handleError,
        onMessage: handleMessage,
      }),
      [handleConnect, handleDisconnect, handleError, handleMessage],
    ),
  );

  const {
    isSpeaking,
    status: conversationStatus,
    startSession: startConversationSession,
    endSession: endConversationSession,
  } = conversation;

  // Keep refs for the memoized handlers.
  useEffect(() => {
    brainRouterRef.current = routeUtterance;
  }, [routeUtterance]);

  // Allow any page to open the assistant via window.dispatchEvent(new CustomEvent('bridgette-open'))
  useEffect(() => {
    const open = () => setIsExpanded(true);
    window.addEventListener("bridgette-open", open);
    return () => window.removeEventListener("bridgette-open", open);
  }, []);

  useEffect(() => {
    const handleQuoteVisibility = (event: Event) => {
      const customEvent = event as CustomEvent<{ open?: boolean }>;
      setInstantQuoteOpen(Boolean(customEvent.detail?.open));
    };

    window.addEventListener("instant-quote-visibility", handleQuoteVisibility as EventListener);
    return () => window.removeEventListener("instant-quote-visibility", handleQuoteVisibility as EventListener);
  }, []);

  const startSession = async () => {
    if (
      sessionStartLockRef.current ||
      conversationStatus === "connecting" ||
      conversationStatus === "connected"
    ) {
      return;
    }
    sessionStartLockRef.current = true;
    setTranscript([]);
    setVoiceStatus("connecting");

    let stream: MediaStream | null = null;
    try {
      if (permissionStreamRef.current) {
        permissionStreamRef.current.getTracks().forEach((t) => t.stop());
        permissionStreamRef.current = null;
      }

      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
        },
      });
      permissionStreamRef.current = stream;

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Sign in required");

      const { data, error } = await supabase.functions.invoke("elevenlabs-conversation-token", {
        body: { agentId: AGENT_ID },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (error) throw new Error(error.message || "Failed to get ElevenLabs conversation token");
      if (!data?.token) throw new Error(data?.error || "No conversation token received");

      await startConversationSession({
        conversationToken: data.token as string,
        connectionType: "webrtc",
        dynamicVariables: {
          current_path: location.pathname,
          user_first_name: firstName || "there",
        },
      });
    } catch (error: any) {
      console.error("Failed to start Bridgette voice session:", error);
      sessionStartLockRef.current = false;
      setVoiceStatus("idle");
      if (stream) stream.getTracks().forEach((t) => t.stop());
      permissionStreamRef.current = null;
      if (error?.name === "NotAllowedError") {
        toast.error("Microphone access denied. Please allow mic access and try again.");
      } else {
        toast.error(error?.message || "Failed to start voice session.");
      }
    }
  };

  const endSession = async () => {
    try {
      await endConversationSession();
    } catch (e) {
      console.error("Error ending ElevenLabs session:", e);
    } finally {
      sessionStartLockRef.current = false;
      if (permissionStreamRef.current) {
        permissionStreamRef.current.getTracks().forEach((t) => t.stop());
        permissionStreamRef.current = null;
      }
      setVoiceStatus("idle");
      setIsExpanded(false);
      setTranscript([]);
    }
  };

  const status = isSpeaking
    ? "speaking"
    : conversationStatus === "connected"
      ? "connected"
      : conversationStatus === "connecting"
        ? "connecting"
        : voiceStatus;

  const statusLabel =
    status === "connecting"
      ? "Connecting…"
      : status === "speaking"
        ? "Bridgette is speaking"
        : status === "connected"
          ? "Listening…"
          : "Tap the mic to begin";

  return (
    <>
      {!hiddenOnCurrentRoute && <BridgetteApprovalDialog pending={pendingApproval} onApprove={approve} onCancel={cancel} />}

      {/* Floating Bridgette bubble — always visible */}
      {!hiddenOnCurrentRoute && !isExpanded && !instantQuoteOpen && (
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          aria-label="Open Bridgette"
          className={cn(
            "fixed z-[9998] flex items-center justify-center rounded-full shadow-2xl transition-all hover:scale-105 active:scale-95",
            "bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground",
            "ring-2 ring-primary/30 hover:ring-primary/50",
            isMobile ? "bottom-24 right-4 h-14 w-14" : "bottom-6 right-6 h-16 w-16",
          )}
        >
          <Mic className={cn(isMobile ? "h-6 w-6" : "h-7 w-7")} />
          <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping opacity-30" />
        </button>
      )}

      {!hiddenOnCurrentRoute && isExpanded && (
        <div
          className={cn(
            "fixed z-[9999] animate-in slide-in-from-right duration-300",
            isMobile
              ? "inset-0"
              : "top-4 bottom-4 right-4 w-[440px]",
          )}
        >
          <div
            className={cn(
              "relative flex h-full flex-col overflow-hidden border border-border/60 shadow-2xl",
              isMobile ? "rounded-none" : "rounded-3xl",
              "bg-gradient-to-br from-background via-background to-primary/5",
            )}
          >
            {/* Ambient glow background */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <div
                className={cn(
                  "absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl transition-opacity duration-700",
                  status === "speaking"
                    ? "bg-primary/40 opacity-100 animate-pulse"
                    : status === "connected"
                      ? "bg-primary/25 opacity-80"
                      : "bg-primary/10 opacity-50",
                )}
              />
              <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-accent/20 blur-3xl opacity-60" />
            </div>

            {/* Header */}
            <div className="relative flex items-center justify-between px-6 pt-5 pb-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative">
                  <div
                    className={cn(
                      "h-9 w-9 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg shadow-primary/30",
                      (status === "connected" || status === "speaking") && "ring-2 ring-primary/40 ring-offset-2 ring-offset-background",
                    )}
                  >
                    <span className="text-sm font-bold text-primary-foreground">B</span>
                  </div>
                  <span
                    className={cn(
                      "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background",
                      status === "connected" || status === "speaking"
                        ? "bg-success animate-pulse"
                        : status === "connecting"
                          ? "bg-warning animate-pulse"
                          : "bg-muted-foreground/40",
                    )}
                  />
                </div>
                <div className="min-w-0">
                  <div className="text-base font-semibold tracking-tight">Bridgette</div>
                  <div className="text-xs text-muted-foreground truncate">{statusLabel}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="inline-flex rounded-md border border-border bg-background/60 p-0.5">
                  {(["en", "ru"] as const).map((l) => (
                    <button
                      key={l}
                      type="button"
                      disabled={status === "connected" || status === "speaking" || status === "connecting"}
                      onClick={() => {
                        setLang(l);
                        try { window.localStorage.setItem("bridgette_lang", l); } catch {}
                      }}
                      className={cn(
                        "px-2 py-1 text-[11px] font-semibold rounded-sm transition-colors",
                        lang === l
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground",
                        (status === "connected" || status === "speaking" || status === "connecting") &&
                          "opacity-60 cursor-not-allowed",
                      )}
                      aria-label={l === "en" ? "English" : "Russian"}
                    >
                      {l === "en" ? "EN" : "RU"}
                    </button>
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={async () => {
                    if (status !== "idle") await endSession();
                    setIsExpanded(false);
                  }}
                  className="h-9 w-9 rounded-full hover:bg-destructive/10"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Voice orb hero */}
            <div className="relative flex flex-1 items-center justify-center px-6 min-h-0">
              <div className="relative">
                <VoiceOrb
                  isActive={status === "connected" || status === "speaking"}
                  isSpeaking={isSpeaking || status === "speaking"}
                />
              </div>
            </div>

            {/* Transcript */}
            {transcript.length > 0 && (
              <div className="relative mx-4 mb-3 max-h-48 overflow-y-auto rounded-2xl border border-border/40 bg-card/60 backdrop-blur-sm px-4 py-3 space-y-2.5">
                {transcript.slice(-6).map((msg, i) => (
                  <div
                    key={i}
                    className={cn(
                      "text-sm leading-relaxed animate-in fade-in slide-in-from-bottom-1 duration-300",
                      msg.role === "agent" ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    <span
                      className={cn(
                        "mr-1.5 text-[10px] font-semibold uppercase tracking-wider",
                        msg.role === "agent" ? "text-primary" : "text-muted-foreground/70",
                      )}
                    >
                      {msg.role === "agent" ? "Bridgette" : "You"}
                    </span>
                    <div>{msg.text}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Action button */}
            <div className="relative flex items-center justify-center gap-2 px-6 pb-6 pt-2">
              {status === "idle" ? (
                <Button
                  onClick={startSession}
                  size="lg"
                  className="flex-1 h-14 rounded-2xl font-semibold text-base shadow-lg shadow-primary/30 bg-gradient-to-r from-primary to-primary/85 hover:opacity-90 transition-all"
                >
                  <Mic className="w-5 h-5 mr-2" />
                  Start talking
                </Button>
              ) : (
                <Button
                  onClick={endSession}
                  variant="destructive"
                  size="lg"
                  className="flex-1 h-14 rounded-2xl font-semibold text-base shadow-lg"
                >
                  <MicOff className="w-5 h-5 mr-2" />
                  End session
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
